import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { HelpPanel } from '../components/HelpPanel';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { useAuthorization } from '../auth/useAuthorization';
import { UserAvatar } from '../components/UserAvatar';
import api from '../api/client';
import { useTheme } from '../theme/ThemeContext';
import { useO365ProfileSync } from '../hooks/useO365ProfileSync';
import { useIntegrations } from '../o365/hooks';
import styles from './AppLayout.module.css';
import { ReleaseNotesModal } from '../components/ReleaseNotesModal';

interface ExternalLink {
  id: string;
  label: string;
  url: string;
  sortOrder: number;
  customIcon?: string;
}

/** Favicon ueber Backend-Proxy laden (cached in Redis) */
function FaviconImg({
  url,
  label,
  customIcon,
}: {
  url: string;
  label: string;
  customIcon?: string;
}) {
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const fetchFavicon = useCallback(async () => {
    try {
      new URL(url);
      const res = await api.get<{ faviconUrl: string | null }>(
        `/settings/favicon?url=${encodeURIComponent(url)}`,
      );
      if (res.data.faviconUrl) {
        setFaviconUrl(res.data.faviconUrl);
        setFailed(false);
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
  }, [url]);

  useEffect(() => {
    fetchFavicon();
  }, [fetchFavicon]);

  // Eigenes Icon hat Vorrang
  if (customIcon) {
    return (
      <img
        src={customIcon}
        alt=""
        style={{
          width: 16,
          height: 16,
          objectFit: 'contain',
          borderRadius: 2,
        }}
      />
    );
  }

  if (failed || !faviconUrl) {
    return (
      <span
        style={{
          display: 'flex',
          width: 16,
          height: 16,
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.625rem',
          fontWeight: 700,
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 2,
          color: 'rgba(255,255,255,0.8)',
          flexShrink: 0,
        }}
      >
        {label.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={faviconUrl}
      alt=""
      style={{
        width: 16,
        height: 16,
        objectFit: 'contain',
        borderRadius: 2,
      }}
      onError={() => setFailed(true)}
    />
  );
}

const THEME_OPTIONS = [
  { value: 'light' as const, label: 'Hell', icon: '☀' },
  { value: 'dark' as const, label: 'Dunkel', icon: '☾' },
  { value: 'system' as const, label: 'System', icon: '⚙' },
];

const BANNER_HEIGHT = '2rem';

function isTestEnvironment(): boolean {
  if (import.meta.env.VITE_SYSTEM_ENV === 'tst') return true;
  const h = window.location.hostname;
  return h.includes('tst') || /^172\.20\.10\.\d+$/.test(h);
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useTheme();
  const { canViewModule, canAdminModule, isPlatformAdmin } = useAuthorization();
  const showTestBanner = isTestEnvironment();

  // Module sind nur sichtbar fuer PLATFORM_ADMIN oder User mit entsprechender Berechtigung
  const canSeeCrm = canViewModule('crm');
  const canSeeCrmAdmin = canAdminModule('crm');
  const canSeeExpertProfile = isPlatformAdmin || canViewModule('expert-profile');
  const canSeeInfoHub = isPlatformAdmin || canViewModule('infohub');
  const canSeeCtb = canViewModule('ctb');
  const canSeeCtbAdmin = canAdminModule('ctb');

  // Silently sync INSIGHT profile from O365 once per browser session
  useO365ProfileSync();
  const [appsOpen, setAppsOpen] = useState(false);
  const [infohubOpen, setInfohubOpen] = useState(location.pathname.startsWith('/infohub'));
  const [ctbOpen, setCtbOpen] = useState(location.pathname.startsWith('/ctb'));
  const [helpOpen, setHelpOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(location.pathname.startsWith('/crm'));
  const [m365Open, setM365Open] = useState(
    ['/emails', '/calendar', '/tasks', '/outlook-contacts'].some((p) => location.pathname.startsWith(p)),
  );

  // M365 Verbindungsstatus
  const { data: integrationsData } = useIntegrations();
  const isO365Connected = integrationsData?.data?.some(
    (i: any) => i.provider === 'MICROSOFT_365' && i.connected,
  ) ?? false;
  // pageKey fuer kontextuelle Hilfe — CRM Tabs brauchen Query-Parameter
  const pageKey = (() => {
    const path = location.pathname;
    const search = location.search;
    if (path === '/crm') {
      if (search.includes('contacts')) return 'crm-contacts';
      if (search.includes('settings') || search.includes('permissions') || search.includes('master-data')) return 'crm-admin';
      return 'crm-companies';
    }
    if (path.startsWith('/crm/companies/')) return 'crm-company-detail';
    if (path.startsWith('/crm/contacts/')) return 'crm-contact-detail';
    return path.replace(/^\//, '').replace(/\//g, '-');
  })();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  const { data: externalLinks } = useQuery<ExternalLink[]>({
    queryKey: ['settings', 'external-links'],
    queryFn: async () => {
      const res = await api.get<ExternalLink[]>('/settings/external-links');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: branding } = useQuery<{
    logo: string | null;
    favicon: string | null;
    sidebarColor: string | null;
    logoWidth: number | null;
    sidebarWidth: number | null;
    primaryColor: string | null;
  }>({
    queryKey: ['settings', 'branding'],
    queryFn: async () => {
      const res = await api.get<{
        logo: string | null;
        favicon: string | null;
        sidebarColor: string | null;
        logoWidth: number | null;
        sidebarWidth: number | null;
        primaryColor: string | null;
      }>('/settings/branding');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Primärfarbe als CSS-Variable setzen
  useEffect(() => {
    const root = document.documentElement;
    const hex = branding?.primaryColor;
    if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      const rh = Math.round(r * 0.82);
      const gh = Math.round(g * 0.82);
      const bh = Math.round(b * 0.82);
      const hoverHex = `#${rh.toString(16).padStart(2, '0')}${gh.toString(16).padStart(2, '0')}${bh.toString(16).padStart(2, '0')}`;
      root.style.setProperty('--color-primary', hex);
      root.style.setProperty('--color-primary-hover', hoverHex);
      root.style.setProperty('--color-primary-light', `rgba(${r}, ${g}, ${b}, 0.12)`);
    } else {
      root.style.removeProperty('--color-primary');
      root.style.removeProperty('--color-primary-hover');
      root.style.removeProperty('--color-primary-light');
    }
  }, [branding?.primaryColor]);

  // Favicon dynamisch setzen
  useEffect(() => {
    const faviconData = branding?.favicon;
    if (!faviconData) return;

    // Bestehendes <link rel="icon"> finden oder erstellen
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconData;
    // Typ aus Data-URL ableiten
    if (faviconData.startsWith('data:image/png')) {
      link.type = 'image/png';
    } else if (faviconData.startsWith('data:image/svg')) {
      link.type = 'image/svg+xml';
    } else if (faviconData.startsWith('data:image/x-icon') || faviconData.startsWith('data:image/vnd.microsoft.icon')) {
      link.type = 'image/x-icon';
    }
  }, [branding?.favicon]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
      {showTestBanner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          height: BANNER_HEIGHT, background: '#f59e0b', color: '#78350f',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.06em',
          userSelect: 'none',
        }}>
          TESTSYSTEM — Keine Produktivdaten eingeben
        </div>
      )}
    <div className={styles.layout} style={showTestBanner ? { paddingTop: BANNER_HEIGHT } : undefined}>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className={styles.mobileOverlay}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''} ${mobileMenuOpen ? styles.sidebarMobileOpen : ''}`}
        style={{
          ...(branding?.sidebarColor ? { background: branding.sidebarColor } : {}),
          ...(!collapsed && branding?.sidebarWidth ? { width: branding.sidebarWidth } : {}),
          ...(showTestBanner ? { top: BANNER_HEIGHT } : {}),
        }}
      >
        <div className={styles.brand}>
          {!collapsed &&
            (branding?.logo ? (
              <img
                src={branding.logo}
                alt="Logo"
                style={{
                  maxHeight: 44,
                  maxWidth: branding.logoWidth ?? 160,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <h2>INSIGHT</h2>
            ))}
          <button
            className={styles.collapseBtn}
            onClick={toggleCollapsed}
            title={collapsed ? 'Menue ausklappen' : 'Menue einklappen'}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {collapsed ? (
                <>
                  <path d="M3 4.5h12M3 9h12M3 13.5h12" />
                </>
              ) : (
                <>
                  <path d="M11 4l-5 5 5 5" />
                </>
              )}
            </svg>
          </button>
        </div>

        <nav className={styles.nav}>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ''}`
            }
            title="Dashboard"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 6l6-4 6 4v7a1 1 0 01-1 1H3a1 1 0 01-1-1V6z" />
              <path d="M6 14V8h4v6" />
            </svg>
            {!collapsed && 'Dashboard'}
          </NavLink>

          {/* Microsoft 365 — nur wenn verbunden */}
          {isO365Connected && (!collapsed ? (
            <button
              className={styles.navSectionToggle}
              onClick={() => setM365Open((p) => !p)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.navSectionIcon}>
                <rect x="1" y="1" width="14" height="14" rx="2" />
                <path d="M5 1v14" />
                <path d="M1 5h4M1 8h14M1 11h4" />
              </svg>
              <span className={styles.navSectionLabel}>Outlook</span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" className={`${styles.chevron} ${m365Open ? styles.chevronOpen : ''}`}>
                <path d="M3 4.5l3 3 3-3" />
              </svg>
            </button>
          ) : isO365Connected ? (
            <div className={styles.navDivider} />
          ) : null)}

          {isO365Connected && (m365Open || collapsed) && (
            <>
              <NavLink
                to="/emails"
                className={({ isActive }) =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`
                }
                title="E-Mail"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="14" height="10" rx="1" />
                  <path d="M1 4l7 5 7-5" />
                </svg>
                {!collapsed && 'E-Mail'}
              </NavLink>
              <NavLink
                to="/calendar"
                className={({ isActive }) =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`
                }
                title="Kalender"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="12" height="11" rx="1" />
                  <path d="M5 1v3M11 1v3M2 7h12" />
                </svg>
                {!collapsed && 'Kalender'}
              </NavLink>
              <NavLink
                to="/tasks"
                className={({ isActive }) =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`
                }
                title="Aufgaben"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 8l3 3 5-6" />
                  <rect x="1" y="1" width="14" height="14" rx="2" />
                </svg>
                {!collapsed && 'Aufgaben'}
              </NavLink>
              <NavLink
                to="/outlook-contacts"
                className={({ isActive }) =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`
                }
                title="Outlook Kontakte"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 14v-1a3 3 0 00-3-3H5a3 3 0 00-3 3v1" />
                  <circle cx="6.5" cy="5" r="3" />
                  <path d="M14 14v-1a3 3 0 00-2-2.83" />
                  <path d="M10 2.17a3 3 0 010 5.66" />
                </svg>
                {!collapsed && 'Kontakte'}
              </NavLink>
            </>
          )}

          {/* CRM-Bereich (aufklappbar) — nur wenn User CRM-Berechtigung hat */}
          {canSeeCrm && (!collapsed ? (
            <button
              className={styles.navSectionToggle}
              onClick={() => setCrmOpen((p) => !p)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.navSectionIcon}
              >
                <rect x="1" y="1" width="14" height="14" rx="2" />
                <path d="M4 5h8M4 8h5M4 11h6" />
              </svg>
              <span className={styles.navSectionLabel}>CRM</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`${styles.chevron} ${crmOpen ? styles.chevronOpen : ''}`}
              >
                <path d="M3 4.5l3 3 3-3" />
              </svg>
            </button>
          ) : (
            <div className={styles.navDivider} />
          ))}

          {canSeeCrm && (crmOpen || collapsed) && (
            <>
              <NavLink
                to="/crm?tab=companies"
                className={() =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${location.pathname === '/crm' && (!location.search || location.search.includes('companies')) ? styles.active : ''}`
                }
                title="Unternehmen"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 14V3a1 1 0 011-1h4a1 1 0 011 1v11" />
                  <path d="M8 7h5a1 1 0 011 1v6" />
                  <path d="M4 5h2M4 7h2M4 9h2M4 11h2" />
                  <path d="M10 10h2M10 12h2" />
                  <path d="M1 14h14" />
                </svg>
                {!collapsed && 'Unternehmen'}
              </NavLink>

              <NavLink
                to="/crm?tab=contacts"
                className={() =>
                  `${collapsed ? styles.navLink : styles.navSubLink} ${location.pathname === '/crm' && location.search.includes('contacts') ? styles.active : ''}`
                }
                title="Kontakte"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 14v-1a3 3 0 00-3-3H5a3 3 0 00-3 3v1" />
                  <circle cx="6.5" cy="5" r="3" />
                  <path d="M14 14v-1a3 3 0 00-2-2.83" />
                  <path d="M10 2.17a3 3 0 010 5.66" />
                </svg>
                {!collapsed && 'Kontakte'}
              </NavLink>

              {/* CRM Admin — PLATFORM_ADMIN oder crm.admin Berechtigung */}
              {canSeeCrmAdmin && (
                <NavLink
                  to="/crm?tab=master-data"
                  className={() =>
                    `${collapsed ? styles.navLink : styles.navSubLink} ${location.pathname === '/crm' && (location.search.includes('settings') || location.search.includes('permissions') || location.search.includes('master-data')) ? styles.active : ''}`
                  }
                  title="CRM Admin"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="8" cy="8" r="2.5" />
                    <path d="M13.5 8a5.5 5.5 0 01-.28 1.72l1.14.66a.5.5 0 01.18.68l-1 1.73a.5.5 0 01-.68.18l-1.14-.66a5.5 5.5 0 01-1.5.87v1.32a.5.5 0 01-.5.5h-2a.5.5 0 01-.5-.5v-1.32a5.5 5.5 0 01-1.5-.87l-1.14.66a.5.5 0 01-.68-.18l-1-1.73a.5.5 0 01.18-.68l1.14-.66A5.5 5.5 0 012.5 8c0-.6.1-1.17.28-1.72l-1.14-.66a.5.5 0 01-.18-.68l1-1.73a.5.5 0 01.68-.18l1.14.66a5.5 5.5 0 011.5-.87V1.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1.32a5.5 5.5 0 011.5.87l1.14-.66a.5.5 0 01.68.18l1 1.73a.5.5 0 01-.18.68l-1.14.66c.18.55.28 1.12.28 1.72z" />
                  </svg>
                  {!collapsed && 'CRM Admin'}
                </NavLink>
              )}
            </>
          )}

          {/* Experten Profil — nur wenn User Berechtigung hat */}
          {canSeeExpertProfile && (
            <NavLink
              to="/expert-profile"
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.active : ''}`
              }
              title="Experten Profil"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="8" cy="5" r="3" />
                <path d="M2 14c0-2.761 2.686-5 6-5s6 2.239 6 5" />
                <path d="M12 4l1 1-3 3-1.5-.5L12 4z" />
              </svg>
              {!collapsed && 'Experten Profil'}
            </NavLink>
          )}

          {/* Info Hub (aufklappbar) — nur sichtbar wenn Berechtigung */}
          {canSeeInfoHub && (
            <>
              {!collapsed ? (
                <button
                  className={styles.navSectionToggle}
                  onClick={() => setInfohubOpen((p) => !p)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.navSectionIcon}>
                    <rect x="1" y="2" width="14" height="12" rx="1.5" />
                    <line x1="8" y1="2" x2="8" y2="14" />
                    <line x1="1" y1="6" x2="8" y2="6" />
                    <line x1="8" y1="8" x2="15" y2="8" />
                  </svg>
                  <span className={styles.navSectionLabel}>Info Hub</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`${styles.chevron} ${infohubOpen ? styles.chevronOpen : ''}`}>
                    <path d="M3 4.5l3 3 3-3" />
                  </svg>
                </button>
              ) : (
                <div className={styles.navDivider} />
              )}
              {(infohubOpen || collapsed) && (
                <>
                  <NavLink to="/infohub" end className={({ isActive }) => `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`} title="News Feed">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="12" height="12" rx="2"/><path d="M2 10h4l1 2h2l1-2h4"/></svg>
                    {!collapsed && 'News Feed'}
                  </NavLink>
                  <NavLink to="/infohub/feeds" className={({ isActive }) => `${collapsed ? styles.navLink : styles.navSubLink} ${isActive ? styles.active : ''}`} title="RSS Feeds">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11a5 5 0 015 5M3 7a9 9 0 019 9"/><circle cx="4" cy="14" r="1.5" fill="currentColor"/></svg>
                    {!collapsed && 'RSS Feeds'}
                  </NavLink>
                  <NavLink to="/infohub/admin/quellen" className={({ isActive }) => `${collapsed ? styles.navLink : styles.navSubLink} ${isActive || location.pathname.startsWith('/infohub/admin') ? styles.active : ''}`} title="Einstellungen">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="2.5"/><path d="M13.4 10a1.1 1.1 0 00.2 1.2l.04.04a1.33 1.33 0 11-1.88 1.88l-.04-.04a1.1 1.1 0 00-1.2-.2 1.1 1.1 0 00-.67 1.01V14a1.33 1.33 0 01-2.67 0v-.06a1.1 1.1 0 00-.72-1.01 1.1 1.1 0 00-1.2.2l-.04.04a1.33 1.33 0 11-1.88-1.88l.04-.04A1.1 1.1 0 003.38 10a1.1 1.1 0 00-1.01-.67H2a1.33 1.33 0 010-2.67h.06a1.1 1.1 0 001.01-.72 1.1 1.1 0 00-.2-1.2l-.04-.04a1.33 1.33 0 111.88-1.88l.04.04A1.1 1.1 0 006 3.38a1.1 1.1 0 00.67-1.01V2a1.33 1.33 0 012.67 0v.06a1.1 1.1 0 00.67 1.01 1.1 1.1 0 001.2-.2l.04-.04a1.33 1.33 0 111.88 1.88l-.04.04a1.1 1.1 0 00-.2 1.2 1.1 1.1 0 001.01.67H14a1.33 1.33 0 010 2.67h-.06a1.1 1.1 0 00-1.01.67z"/></svg>
                    {!collapsed && 'Einstellungen'}
                  </NavLink>
                </>
              )}
            </>
          )}

          {/* Compliance Tool Box (aufklappbar) */}
          {canSeeCtb && (
            <>
              {!collapsed ? (
                <button
                  className={styles.navSectionToggle}
                  onClick={() => setCtbOpen((p: boolean) => !p)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.navSectionIcon}>
                    <rect x="2" y="2" width="12" height="12" rx="2" />
                    <path d="M5.5 8l2 2 3.5-4" />
                  </svg>
                  <span className={styles.navSectionLabel}>Compliance Tool Box</span>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" className={`${styles.chevron} ${ctbOpen ? styles.chevronOpen : ''}`}>
                    <path d="M3 4.5l3 3 3-3" />
                  </svg>
                </button>
              ) : (
                <NavLink to="/ctb" className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`} title="Compliance Tool Box">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="12" height="12" rx="2" />
                    <path d="M5.5 8l2 2 3.5-4" />
                  </svg>
                </NavLink>
              )}
              {!collapsed && ctbOpen && (
                <>
                  <NavLink to="/ctb" end className={({ isActive }) => `${styles.navSubLink} ${isActive ? styles.active : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="12" height="11" rx="1"/><path d="M5 1v3M11 1v3M2 7h12"/></svg>
                    Dashboard
                  </NavLink>
                  <NavLink to="/ctb/reporting" className={({ isActive }) => `${styles.navSubLink} ${isActive ? styles.active : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12l4-4 3 3 5-6"/><rect x="2" y="2" width="12" height="12" rx="1"/></svg>
                    Plan vs. Ist
                  </NavLink>
                  {canSeeCtbAdmin && (
                    <NavLink to="/ctb/admin" className={({ isActive }) => `${styles.navSubLink} ${isActive ? styles.active : ''}`}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="2.5"/><path d="M13.4 10a1.1 1.1 0 00.2 1.2l.04.04a1.33 1.33 0 11-1.88 1.88l-.04-.04a1.1 1.1 0 00-1.2-.2 1.1 1.1 0 00-.67 1.01V14a1.33 1.33 0 01-2.67 0v-.06a1.1 1.1 0 00-.72-1.01 1.1 1.1 0 00-1.2.2l-.04.04a1.33 1.33 0 11-1.88-1.88l.04-.04A1.1 1.1 0 003.38 10a1.1 1.1 0 00-1.01-.67H2a1.33 1.33 0 010-2.67h.06a1.1 1.1 0 001.01-.72 1.1 1.1 0 00-.2-1.2l-.04-.04a1.33 1.33 0 111.88-1.88l.04.04A1.1 1.1 0 006 3.38a1.1 1.1 0 00.67-1.01V2a1.33 1.33 0 012.67 0v.06a1.1 1.1 0 00.67 1.01 1.1 1.1 0 001.2-.2l.04-.04a1.33 1.33 0 111.88 1.88l-.04.04a1.1 1.1 0 00-.2 1.2 1.1 1.1 0 001.01.67H14a1.33 1.33 0 010 2.67h-.06a1.1 1.1 0 00-1.01.67z"/></svg>
                      CTB Admin
                    </NavLink>
                  )}
                </>
              )}
            </>
          )}

          {/* Externe Links (aufklappbar) */}
          {externalLinks && externalLinks.length > 0 && (
            <>
              {!collapsed ? (
                <button
                  className={styles.navSectionToggle}
                  onClick={() => setAppsOpen((p) => !p)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={styles.navSectionIcon}>
                    <path d="M9 2H14V7" />
                    <path d="M14 2L8 8" />
                    <path d="M12 9v4a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4" />
                  </svg>
                  <span className={styles.navSectionLabel}>Externe Anwendungen</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={`${styles.chevron} ${appsOpen ? styles.chevronOpen : ''}`}
                  >
                    <path d="M3 4.5l3 3 3-3" />
                  </svg>
                </button>
              ) : (
                <div className={styles.navDivider} />
              )}
              {(appsOpen || collapsed) &&
                externalLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(link.url, link.label, 'popup,noopener');
                    }}
                    className={styles.navLink}
                    title={link.label}
                  >
                    <FaviconImg url={link.url} label={link.label} customIcon={link.customIcon} />
                    {!collapsed && (
                      <>
                        {link.label}
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          style={{ marginLeft: 'auto', opacity: 0.4 }}
                        >
                          <path d="M6 1h3v3" />
                          <path d="M4 6l5-5" />
                        </svg>
                      </>
                    )}
                  </a>
                ))}
            </>
          )}
        </nav>

        {/* Admin-Link (nur PLATFORM_ADMIN) */}
        {user?.role === 'PLATFORM_ADMIN' && (
          <div className={styles.adminSection}>
            <NavLink
              to="/admin/users"
              className={({ isActive }) =>
                `${styles.adminLink} ${isActive ? styles.adminLinkActive : ''}`
              }
              title="Administration"
              style={({ isActive }) => {
                const isAdminPath =
                  window.location.pathname.startsWith('/admin');
                return isAdminPath && !isActive
                  ? {
                      color: 'white',
                      background: 'rgba(96, 165, 250, 0.15)',
                    }
                  : {};
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="8" cy="8" r="2.5" />
                <path d="M13.5 8a5.5 5.5 0 01-.28 1.72l1.14.66a.5.5 0 01.18.68l-1 1.73a.5.5 0 01-.68.18l-1.14-.66a5.5 5.5 0 01-1.5.87v1.32a.5.5 0 01-.5.5h-2a.5.5 0 01-.5-.5v-1.32a5.5 5.5 0 01-1.5-.87l-1.14.66a.5.5 0 01-.68-.18l-1-1.73a.5.5 0 01.18-.68l1.14-.66A5.5 5.5 0 012.5 8c0-.6.1-1.17.28-1.72l-1.14-.66a.5.5 0 01-.18-.68l1-1.73a.5.5 0 01.68-.18l1.14.66a5.5 5.5 0 011.5-.87V1.5a.5.5 0 01.5-.5h2a.5.5 0 01.5.5v1.32a5.5 5.5 0 011.5.87l1.14-.66a.5.5 0 01.68.18l1 1.73a.5.5 0 01-.18.68l-1.14.66c.18.55.28 1.12.28 1.72z" />
              </svg>
              {!collapsed && 'Administration'}
            </NavLink>
          </div>
        )}

        {/* Profil-Bereich unten in der Sidebar */}
        <div className={styles.sidebarBottom}>
          {/* Feature Requests */}
          <NavLink
            to="/feature-requests"
            className={({ isActive }) =>
              `${styles.navLink} ${isActive ? styles.active : ''}`
            }
            title="Feature Requests"
            style={{ marginBottom: '0.5rem' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 14h4" />
              <path d="M6 12h4" />
              <path d="M5 10c-1.5-1.3-2-3-2-4.5a5 5 0 1110 0c0 1.5-.5 3.2-2 4.5" />
            </svg>
            {!collapsed && 'Feature Requests'}
          </NavLink>

          {/* Zeile 1: Theme-Schalter + Hilfe */}
          <div className={styles.sidebarBottomRow}>
            <div className={styles.sidebarThemeGroup}>
              {THEME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`${styles.sidebarThemeBtn} ${mode === opt.value ? styles.sidebarThemeBtnActive : ''}`}
                  onClick={() => setMode(opt.value)}
                  title={opt.label}
                >
                  {opt.icon}
                </button>
              ))}
            </div>
            <button
              className={styles.sidebarIconBtn}
              onClick={() => setHelpOpen(true)}
              title="Hilfe"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="8" r="7" />
                <path d="M6 6a2 2 0 114 0c0 1-1 1.5-2 2.5" />
                <circle cx="8" cy="12" r="0.75" fill="currentColor" stroke="none" />
              </svg>
            </button>
          </div>

          {/* Zeile 2: Benutzer + Abmelden */}
          <div className={styles.sidebarBottomRow}>
            <div
              className={styles.sidebarUser}
              onClick={() => navigate('/profile')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate('/profile');
              }}
              title="Profil bearbeiten"
              style={{ flex: 1 }}
            >
              <UserAvatar
                firstName={user?.firstName ?? ''}
                lastName={user?.lastName ?? ''}
                avatar={user?.avatar}
                size={32}
              />
              {!collapsed && (
                <span className={styles.sidebarUserName}>
                  {user?.firstName} {user?.lastName}
                </span>
              )}
            </div>
            <button
              className={styles.sidebarIconBtn}
              onClick={handleLogout}
              title="Abmelden"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 3h3a1 1 0 011 1v8a1 1 0 01-1 1h-3" />
                <path d="M7 11l3-3-3-3" />
                <path d="M10 8H3" />
              </svg>
            </button>
          </div>
        </div>

      </aside>

      {/* Hilfe-Panel */}
      <HelpPanel isOpen={helpOpen} onClose={() => setHelpOpen(false)} pageKey={pageKey} />

      {/* Main Content — paddingTop kompensiert TST-Banner wenn sichtbar */}
      <main
        className={styles.main}
        style={{
          marginLeft: collapsed ? 60 : (branding?.sidebarWidth ?? undefined),
          ...(showTestBanner ? { paddingTop: BANNER_HEIGHT } : {}),
        }}
      >
        {/* Mobile Header mit Hamburger */}
        <div style={{
          display: 'none', padding: '0.5rem 0.75rem',
          borderBottom: '1px solid var(--color-border)',
          alignItems: 'center', gap: '0.5rem', background: 'var(--color-bg)',
          position: 'sticky', top: 0, zIndex: 50,
        }} className={styles.mobileHeader}>
          <button
            className={styles.mobileMenuBtn}
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Menü öffnen"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
          <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-text)' }}>INSIGHT</span>
        </div>
        <div className={styles.mainContent}>
          <Outlet />
        </div>
      </main>
    </div>
    <ReleaseNotesModal />
    </>
  );
}
